import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Container from '../../blocks/Container';
import Panel from '../../../atoms/panels/Panel';
import Headline from '../../common/Headline';
import styled, { CSSObject } from '@emotion/styled';
import { getTextColor, type, getBackgroundTheme, getPaymentTheme } from '../../../style';
import DataLayer from '../../../utils/dataLayer';
import BillingSuccessHeader from '../../../components/blocks/headerNav/BillingSuccessHeader';
import { removeLocalItem } from '../../../common';
import { addCommas, customToFixed, ternaryFunc, getCookie, removeCookie, isDarkMode } from '../../../utils/helpers';
import useMixPanel from '../../../hooks/useMixPanel';
import { PROMO_COOKIE } from '../../../config/constants';
import { useApp } from '../../../contexts/AppContext';
import GooglePayIcon from '../../../atoms/billing/GooglePay/GooglePayIcon';
import { SofortIcon } from '../../../atoms/icons/Billing';
import { MIXPANEL_CREDITS_PURCHASED } from '../../../constants/mixpanel';
import ApplePayIcon from '../../../atoms/billing/GooglePay/ApplePayIcon';
import { APAY_PAYMENT_TYPE } from '../../../config/apay';
import { GPAY_PAYMENT_TYPE } from '../../../config/gpay';
import { removeIPQSCookies } from '../../../utils/ipQualityScore';
import breakpoints from '../../../theme/material/breakpoints';
import useBreakpointQuery from '../../../hooks/useBreakPointQuery';
import PrimaryButton from 'atoms/buttons/PrimaryButton';

const StyledHeadline = styled(Headline)(() => ({
  ...getTextColor().NORMAL,
}));

const CreditCardNote = styled('div')(() => ({
  marginTop: 10,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: -0.28,
  color: isDarkMode() ? '#FF5959' : '#DD0B24',
}));

const SubHeader = styled('div')(() => ({
  width: '100%',
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '21px',
  color: isDarkMode() ? '#F6F4F4' : '#353946',
}));

const Row = styled('div')(() => ({
  color: getPaymentTheme().SUCCESS.item.color,
  display: 'flex',
  justifyContent: 'space-between',
  padding: '20px 0',
  margin: '0 15px',
  alignItems: 'center',
  borderBottom: `1px dashed ${getPaymentTheme().DIVIDER.borderColor}`,
  '&:last-of-type': {
    borderBottom: 'none',
    paddingBottom: 20,
  },
  [breakpoints.down(769)]: {
    padding: '12px 0',
    '&:last-of-type': {
      paddingBottom: 15,
    },
  },
}));

const ReferenceRow = styled('div')(() => ({
  color: getPaymentTheme().SUCCESS.item.color,
  display: 'flex',
  justifyContent: 'space-between',
  padding: '20px 0',
  margin: '0 15px',
  alignItems: 'center',
  '&:last-of-type': {
    borderBottom: 'none',
    paddingBottom: 20,
  },
  [breakpoints.down(769)]: {
    padding: '12px 0',
    '&:last-of-type': {
      paddingBottom: 15,
    },
  },
}));

const Total = styled('div')(() => ({
  color: isDarkMode() ? '#FFF' : '#212121',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '15px',
  backgroundColor: getBackgroundTheme().paymentHighlight,
  borderRadius: '0px 0px 6px 6px',
  fontSize: 18,
  lineHeight: '23px',
  letterSpacing: -0.3,
  fontWeight: 700,
  [breakpoints.down(769)]: {
    paddingRight: 15,
    paddingLeft: 15,
  },
}));

const LeftCol = styled('div')(() => ({
  flexGrow: 1,
  textAlign: 'left',
  fontSize: 16,
  ...getTextColor().LIGHT_GRAY,
}));

const LeftColPackage = styled('div')(
  (): CSSObject => ({
    flexGrow: 1,
    textAlign: 'left',
    fontSize: 18,
    color: isDarkMode() ? '#F6F4F4' : '#212121',
    fontWeight: 600,
    lineHeight: '21px',
    letterSpacing: -0.18,
    [breakpoints.down(769.05)]: {
      letterSpacing: -0.3,
    },
  })
);

const RightCol = styled('div')(() => ({
  flexGrow: 10,
  textAlign: 'right',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  ...getTextColor().LIGHT_GRAY,
  fontWeight: 400,
  fontSize: 16,
  letterSpacing: -0.3,
  lineHeight: '21px',
  [breakpoints.down(769)]: {
    fontSize: 15,
    lineHeight: '20px',
  },
}));

const RightColPackage = styled(RightCol)({});
const LeftColTotal = styled(LeftCol)({});

const SubText = styled('div')(() => ({
  ...getTextColor().NORMAL,
  ...type.NORMAL,
  ...type.XS,
}));

const BillingSummary = styled('div')(() => ({
  background: getBackgroundTheme().orderSummary,
  border: isDarkMode() ? 'none' : `1px solid ${getPaymentTheme().SUMMARY.borderColor}`,
  marginTop: 10,
  borderRadius: 6,
}));

const BillingSummaryItems = styled('div')(() => ({
  ...(isDarkMode()
    ? {
        border: `1px solid ${getPaymentTheme().SUMMARY_SECONDARY.borderColor}`,
        borderRadius: '6px 6px 0px 0px',
        borderBottom: 'none',
      }
    : {}),
}));

const CreditText = styled('span')(() => ({
  color: isDarkMode() ? '#F6F4F4' : '#212121',
  fontWeight: 900,
}));

const renderTransactionRow = (transactionID, isMobile) => {
  return (
    <ReferenceRow>
      <LeftCol style={{ fontSize: isMobile ? 14 : 16 }}>Transaction Reference</LeftCol>
      <RightCol style={{ fontSize: isMobile ? 14 : 16 }}>{`${transactionID}`}</RightCol>
    </ReferenceRow>
  );
};

const renderSubtotal = lastPayment => {
  return parseFloat(lastPayment && lastPayment.original_price ? lastPayment.original_price : 0);
};

const renderDiscountAmount = (thePackage, promoCode) => {
  const amount = thePackage.taxes ? thePackage.taxes.taxable_amount : thePackage.price;
  let discountAmount = promoCode.discount_amount ? promoCode.discount_amount : 0;

  if (promoCode.discount_rate) {
    discountAmount = amount * promoCode.discount_rate;
  }

  if (thePackage.taxes && thePackage.taxes.discount_amount) {
    discountAmount = thePackage.taxes.discount_amount ?? 0;
  }

  return customToFixed(discountAmount).toFixed(2);
};

interface BillingSuccessProps {
  payments: any;
  profile: any;
  descriptors: any;
  account: any;
}

const BillingSuccess = ({ payments, profile, descriptors, account }: BillingSuccessProps): any => {
  const { trackCreditsPurchased } = useMixPanel();
  const { setSelectedSpecialOrVipPromo } = useApp();
  const [lastPayment] = useState(payments[0]);
  const isMobile = useBreakpointQuery({ key: 'down', value: 600 });

  useEffect(() => {
    return () => {
      removeLocalItem('wyp_billing_packages');
      removeLocalItem(MIXPANEL_CREDITS_PURCHASED);
      removeIPQSCookies();
    };
  }, []);

  useEffect(() => {
    const lastPackage = lastPayment.package.data;

    new DataLayer().push({
      account_id: account.hash_id,
      submitted_at: profile.submitted_date || null,
      completed_at: profile.completed_date || null,
      monetized_at: profile.monetized_date || null,
      transactionId: lastPayment.id,
      gender: profile.sex,
      age: profile.age,
      availableCredits: account.credits,
      publicPhotos: account.approved_public_photos_count || 0,
      outstandingOffers: account.offer_counts.new,
      type: profile.account_type,
      looking_male: profile.looking_male,
      looking_female: profile.looking_female,
      transactionAffiliation: 'Credit Purchase',
      transactionTotal: lastPayment.charge_amt,
      currencyCode: lastPackage.currency.data.code,
      transactionProducts: [
        {
          sku: `${lastPackage.identifier}`,
          name: `${lastPackage.credits} Credits`,
          category: 'Credit Purchase',
          price: `${lastPackage.price}`,
          currency: lastPackage.currency.data.code,
          quantity: '1',
        },
      ],
      event: 'transactionComplete',
    });
  }, [payments, account, lastPayment, profile]);

  useEffect(() => {
    if (lastPayment.id) {
      let additionalPayload = {};

      if (lastPayment?.promo_code) {
        additionalPayload = {
          promo_code: lastPayment.promo_code.code,
          discount: renderDiscountAmount(lastPayment.package.data, lastPayment.promo_code),
        };

        // Remove promo cookes
        const promoCodeCookies = getCookie(PROMO_COOKIE);

        if (promoCodeCookies && promoCodeCookies === lastPayment?.promo_code.code) {
          removeCookie(PROMO_COOKIE);
        }
      }

      trackCreditsPurchased(additionalPayload);

      setSelectedSpecialOrVipPromo(null);
      removeLocalItem('wyp_billing_packages');
      removeIPQSCookies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPayment.id]);

  const thePackage = lastPayment.package.data;
  const promoCode = lastPayment.promo_code;
  const transactionID = lastPayment.last_transaction_reference;
  const { taxes } = thePackage;

  const renderCurrencyCode = (): string => {
    if (thePackage.currency.data?.code?.toUpperCase()) {
      return ` ${thePackage.currency.data?.code?.toUpperCase()}`;
    }

    return '';
  };

  const renderPaymentMethod = (lastPayment: any): any => {
    if (lastPayment?.method === 'use2pay-AlternativePayments') {
      return <SofortIcon isDarkMode={isDarkMode()} />;
    }

    let paymentMethodIcon: any;

    switch (lastPayment?.purchase_type) {
      case GPAY_PAYMENT_TYPE:
        paymentMethodIcon = (
          <GooglePayIcon width={56.4} height={30} customStyle={{ marginRight: 10 }} darkmode={isDarkMode()} />
        );
        break;
      case APAY_PAYMENT_TYPE:
        paymentMethodIcon = (
          <ApplePayIcon width={56.4} height={30} customStyle={{ marginRight: 5 }} darkmode={isDarkMode()} />
        );
        break;
      default:
        paymentMethodIcon = null;
    }

    return (
      <>
        {paymentMethodIcon}
        <span style={{ margin: '' }}>{lastPayment.card_type}</span>
        {lastPayment?.last_4 && <span style={{ margin: '0 2px', fontFamily: 'normal' }}>••••</span>}
        {lastPayment?.last_4 && <span style={{ margin: '' }}>{lastPayment.last_4}</span>}
      </>
    );
  };

  return (
    <Container
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      padding="thin"
      screenTop
    >
      {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389} */}
      <BillingSuccessHeader label="Boom! You have credits!" isSuccess />
      {lastPayment ? (
        <>
          <Panel
            customStyle={{
              maxWidth: '658px',
              margin: '0 auto',
              border: '2px solid #24A83A',
              backgroundColor: getBackgroundTheme().orderSummary,
              borderRadius: 10,
              padding: '20px',
              [breakpoints.down(769)]: { padding: '20px 10px', border: '2px solid #24A83A' },
            }}
          >
            <SubHeader>Receipt</SubHeader>
            <BillingSummary>
              <BillingSummaryItems>
                <Row>
                  <LeftColPackage>
                    <CreditText>{addCommas(thePackage.credits)}</CreditText>{' '}
                    <span style={{ fontWeight: 400 }}>Credit Package</span>
                  </LeftColPackage>
                  <RightColPackage>
                    {`${String.fromCharCode(thePackage.currency.data.label_unicode)}${parseFloat(
                      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                      renderSubtotal(lastPayment)
                    ).toFixed(2)}`}
                    {renderCurrencyCode()}
                  </RightColPackage>
                </Row>
                {promoCode && (promoCode.discount_amount !== null || promoCode.discount_rate !== null) && (
                  <Row>
                    <LeftCol>Promo Code ({`${promoCode.code}`})</LeftCol>
                    <RightCol>
                      {`- ${String.fromCharCode(thePackage.currency.data.label_unicode)}${renderDiscountAmount(
                        thePackage,
                        promoCode
                      )}`}
                    </RightCol>
                  </Row>
                )}
                {taxes && (
                  <Row>
                    <LeftCol>
                      Estimated tax ({`${thePackage.taxes.location} ${thePackage.taxes.rate * 100}%`})
                      <SubText>(VAT Registration Number: CY10384442N)</SubText>
                    </LeftCol>
                    <RightCol>
                      {`${String.fromCharCode(thePackage.currency.data.label_unicode)}${parseFloat(
                        thePackage.taxes.tax_amount
                      ).toFixed(2)}`}
                    </RightCol>
                  </Row>
                )}
                <Row>
                  <LeftCol>Paid with</LeftCol>
                  <RightCol>{renderPaymentMethod(lastPayment)}</RightCol>
                </Row>
                {ternaryFunc(transactionID, renderTransactionRow(transactionID, isMobile), null)}
              </BillingSummaryItems>
              <Total>
                <LeftColTotal style={{ paddingTop: 5 }}>
                  <span>TOTAL</span> {isMobile && <br />}
                  <span style={{ fontWeight: 'normal' }}>(amount billed today):</span>
                </LeftColTotal>
                <strong>
                  {`${String.fromCharCode(thePackage.currency.data.label_unicode)}${parseFloat(
                    lastPayment.charge_amt
                  ).toFixed(2)}`}
                  {renderCurrencyCode()}
                </strong>
              </Total>
            </BillingSummary>

            <CreditCardNote>
              <b>Note:</b>
              <br /> {descriptors.join(', ').replace(/,(?!.*,)/gim, ' or')} will appear on your credit card statement
              for this purchase.
            </CreditCardNote>
            <div style={{ textAlign: 'center' }}>
              <Link data-test-id="payment-success-dashboard-link" to="/search">
                <PrimaryButton
                  onClick={() => ''}
                  color="blue"
                  customStyle={{
                    fontSize: 18,
                    fontWeight: 700,
                    lineHeight: '16px',
                    letterSpacing: -0.36,
                    width: '280px',
                    height: 'auto',
                    margin: isMobile ? '30px 0 10px 0' : '40px 0 30px 0',
                    padding: '14px 30px',
                  }}
                  label="Browse Profiles"
                />
              </Link>
            </div>
          </Panel>
        </>
      ) : (
        <Panel>
          <StyledHeadline size="SM">No Purchase History!</StyledHeadline>
        </Panel>
      )}
    </Container>
  );
};

export default BillingSuccess;
